/**
 * Walking Tours cart pricing — applies the correct Shopify discount code
 * based on walking-tour ticket quantity (BOGO for pairs, group rate for 10+).
 *
 * Requires matching discount CODES in Shopify admin (not automatic discounts).
 */
(function (global) {
  'use strict';

  var config = null;
  var syncing = false;

  function readConfig() {
    if (config) return config;
    var el = document.getElementById('wt-pricing-config');
    if (!el) return null;
    try {
      config = JSON.parse(el.textContent);
    } catch (e) {
      config = null;
    }
    return config;
  }

  function extractHandle(url) {
    if (!url) return '';
    var match = String(url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function isWalkingTourItem(item, cfg) {
    if (!item || !cfg || !cfg.handles || !cfg.handles.length) return false;
    return cfg.handles.indexOf(extractHandle(item.url)) !== -1;
  }

  function countWalkingTourQty(cart, cfg) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      return sum + (isWalkingTourItem(item, cfg) ? item.quantity : 0);
    }, 0);
  }

  function desiredCode(wtQty, cfg) {
    if (!cfg || !cfg.enabled) return '';
    if (wtQty >= cfg.groupMinQty && cfg.groupCode) return cfg.groupCode;
    if (wtQty >= 2 && cfg.bogoCode) return cfg.bogoCode;
    return '';
  }

  function calcTotalCents(qty, cfg) {
    if (!cfg || qty <= 0) return 0;
    var unit = cfg.unitCents || 0;
    if (qty >= cfg.groupMinQty && cfg.groupTotalCents) return cfg.groupTotalCents;
    if (qty >= 2) {
      var pairs = Math.floor(qty / 2);
      var remainder = qty % 2;
      return pairs * (unit + Math.round(unit / 2)) + remainder * unit;
    }
    return qty * unit;
  }

  function formatMoney(cents) {
    var fmt = global.Shopify && global.Shopify.formatMoney;
    var moneyFormat = (global.theme && global.theme.moneyFormat) || '£{{amount}}';
    if (typeof fmt === 'function') return fmt(cents, moneyFormat);
    return '£' + (cents / 100).toFixed(2);
  }

  function apiRoot() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  function fetchCart() {
    return fetch(apiRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    });
  }

  function syncCartDiscounts() {
    var cfg = readConfig();
    if (!cfg || !cfg.enabled || syncing) {
      return Promise.resolve(null);
    }

    syncing = true;
    return fetchCart()
      .then(function (cart) {
        var wtQty = countWalkingTourQty(cart, cfg);
        var code = desiredCode(wtQty, cfg);
        return fetch(apiRoot() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: code })
        }).then(function (r) {
          if (!r.ok) throw new Error('discount update failed');
          return r.json();
        });
      })
      .finally(function () {
        syncing = false;
      });
  }

  function addWalkingTourItem(variantId, quantity) {
    quantity = Math.max(1, parseInt(quantity, 10) || 1);
    return fetch(apiRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: { _wt: '1' }
      })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function (result) {
      return syncCartDiscounts().then(function () { return result; });
    });
  }

  function bindProductForm(root) {
    var cfg = readConfig();
    if (!cfg || !cfg.enabled || !root) return;

    var form = root.querySelector('form.wtp-form') || root.querySelector('form');
    if (!form || form.dataset.wtPricingBound) return;
    form.dataset.wtPricingBound = 'true';

    var qtyInput = form.querySelector('[name="quantity"]');
    var idInput = form.querySelector('[name="id"]');
    var estimateEl = root.querySelector('[data-wtp-price-estimate]');
    var priceEl = root.querySelector('.wtp-hero__price');

    function updateEstimate() {
      if (!estimateEl || !qtyInput) return;
      var qty = parseInt(qtyInput.value, 10) || 1;
      var total = calcTotalCents(qty, cfg);
      if (qty <= 1) {
        estimateEl.hidden = true;
        estimateEl.textContent = '';
        return;
      }
      estimateEl.hidden = false;
      if (qty >= cfg.groupMinQty) {
        estimateEl.textContent = qty + ' tickets: ' + formatMoney(total) + ' (group rate)';
      } else if (qty >= 2) {
        estimateEl.textContent = qty + ' tickets: ' + formatMoney(total) + ' (bring-a-friend rate)';
      } else {
        estimateEl.textContent = 'Total: ' + formatMoney(total);
      }
    }

    if (qtyInput) {
      qtyInput.addEventListener('input', updateEstimate);
      qtyInput.addEventListener('change', updateEstimate);
      root.querySelectorAll('[data-wtp-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          setTimeout(updateEstimate, 0);
        });
      });
      updateEstimate();
    }

    form.addEventListener('submit', function (e) {
      if (!idInput || !idInput.value) return;
      e.preventDefault();

      var submitBtn = form.querySelector('[type="submit"]');
      var stickyBtn = root.querySelector('[data-wtp-sticky-buy]');
      var originalLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding…';
      }
      if (stickyBtn) stickyBtn.disabled = true;

      addWalkingTourItem(parseInt(idInput.value, 10), qtyInput ? qtyInput.value : 1)
        .then(function () {
          global.location.href = apiRoot() + 'cart';
        })
        .catch(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalLabel;
          }
          if (stickyBtn) stickyBtn.disabled = false;
          form.submit();
        });
    });
  }

  function bindOfferButtons() {
    document.querySelectorAll('[data-wt-offer-add]').forEach(function (btn) {
      if (btn.dataset.wtOfferBound) return;
      btn.dataset.wtOfferBound = 'true';

      btn.addEventListener('click', function () {
        var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
        var qty = parseInt(btn.getAttribute('data-quantity'), 10);
        if (!variantId || !qty || btn.disabled) return;

        var originalLabel = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';

        addWalkingTourItem(variantId, qty)
          .then(function () {
            global.location.href = apiRoot() + 'cart';
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = originalLabel;
          });
      });
    });
  }

  function syncOfferButtons(root, variantId, stock) {
    if (!root) return;
    root.querySelectorAll('[data-wt-offer-add]').forEach(function (btn) {
      if (variantId) btn.setAttribute('data-variant-id', variantId);
      var minStock = parseInt(btn.getAttribute('data-min-stock'), 10);
      if (minStock) {
        btn.disabled = isNaN(stock) || stock < minStock;
      }
    });
  }

  function initCartPage() {
    if (!document.querySelector('[data-cart-root]')) return;
    if (window.location.search.indexOf('wt_synced=1') !== -1) return;

    var cfg = readConfig();
    if (!cfg || !cfg.enabled) return;

    fetchCart().then(function (cart) {
      if (countWalkingTourQty(cart, cfg) === 0) return;
      return syncCartDiscounts().then(function () {
        var url = window.location.pathname + window.location.search;
        url += (url.indexOf('?') >= 0 ? '&' : '?') + 'wt_synced=1';
        window.location.replace(url);
      });
    });
  }

  function initProductPages() {
    document.querySelectorAll('[data-wtp-product]').forEach(bindProductForm);
  }

  global.WalkingToursPricing = {
    readConfig: readConfig,
    calcTotalCents: calcTotalCents,
    formatMoney: formatMoney,
    countWalkingTourQty: countWalkingTourQty,
    desiredCode: desiredCode,
    syncCartDiscounts: syncCartDiscounts,
    addWalkingTourItem: addWalkingTourItem,
    isWalkingTourItem: isWalkingTourItem,
    extractHandle: extractHandle,
    syncOfferButtons: syncOfferButtons
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initProductPages();
      bindOfferButtons();
      initCartPage();
    });
  } else {
    initProductPages();
    bindOfferButtons();
    initCartPage();
  }
})(window);
